import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, Code2, Zap, LogOut, User, Loader, Save, GitBranch } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { projectsAPI } from '../api';

const STORAGE_KEY = 'triggerdocs_project';

function Dashboard() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [projectName, setProjectName] = useState('My Project');
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const fileInputRef = useRef(null);

  // Load project from backend on mount
  useEffect(() => {
    const loadProject = async () => {
      try {
        const savedProjectId = localStorage.getItem(STORAGE_KEY);
        if (savedProjectId) {
          // Load specific project by ID
          const projects = await projectsAPI.getAll(token);
          const project = projects.find(p => p.id === parseInt(savedProjectId));
          if (project) {
            setProjectName(project.name);
            setCurrentProjectId(project.id);

            // Check size before parsing to prevent browser crashes
            const dataSize = new Blob([project.files_data || '[]']).size / (1024 * 1024);
            console.log(`Loading project with data size: ${dataSize.toFixed(2)}MB`);

            if (dataSize > 100) {
              alert(`Project data is too large (${dataSize.toFixed(2)}MB) to load safely. Starting with empty project instead.`);
              setFiles([]);
            } else {
              const filesData = JSON.parse(project.files_data || '[]');
              setFiles(filesData);
            }
          }
        } else {
          // Load most recent project
          const projects = await projectsAPI.getAll(token);
          if (projects.length > 0) {
            const latest = projects[0];
            setProjectName(latest.name);
            setCurrentProjectId(latest.id);

            // Check size before parsing to prevent browser crashes
            const dataSize = new Blob([latest.files_data || '[]']).size / (1024 * 1024);
            console.log(`Loading project with data size: ${dataSize.toFixed(2)}MB`);

            if (dataSize > 100) {
              alert(`Project data is too large (${dataSize.toFixed(2)}MB) to load safely. Starting with empty project instead.`);
              setFiles([]);
            } else {
              const filesData = JSON.parse(latest.files_data || '[]');
              setFiles(filesData);
            }
            localStorage.setItem(STORAGE_KEY, latest.id.toString());
          }
        }
      } catch (error) {
        console.error('Failed to load project:', error);
        alert('Failed to load project. Starting fresh. Error: ' + error.message);
        setFiles([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [token]);

  // Auto-save disabled to prevent issues with large file sets
  // Users can manually save or it will save on logout/navigation
  // useEffect(() => {
  //   if (files.length > 0 && !isLoading) {
  //     saveProject();
  //   }
  // }, [files]);

  const saveProject = async () => {
    if (files.length === 0) {
      alert('No files to save');
      return;
    }

    setIsSaving(true);
    try {
      const filesData = JSON.stringify(files);

      // Log payload size for debugging
      const payloadSizeMB = new Blob([filesData]).size / (1024 * 1024);
      console.log(`Saving project with ${files.length} files, payload size: ${payloadSizeMB.toFixed(2)}MB`);

      // Check if payload exceeds reasonable limit (50MB for most servers)
      if (payloadSizeMB > 50) {
        const message = `Project is too large (${payloadSizeMB.toFixed(2)}MB). The maximum size is 50MB.\n\nCurrent: ${files.length} files\nTry selecting only the most important files for your documentation.`;
        alert(message);
        setIsSaving(false);
        return;
      }

      if (currentProjectId) {
        // Update existing project
        await projectsAPI.update(token, currentProjectId, projectName, filesData);
        console.log('[SAVE SUCCESS] Project updated');
        alert('Project saved successfully!');
      } else {
        // Create new project
        const newProject = await projectsAPI.create(token, projectName, filesData);
        setCurrentProjectId(newProject.id);
        localStorage.setItem(STORAGE_KEY, newProject.id.toString());
        console.log('[SAVE SUCCESS] New project created with ID:', newProject.id);
        alert('Project created successfully!');
      }
    } catch (error) {
      console.error('[SAVE ERROR] Failed to save project:', error);

      // Provide more helpful error messages
      let errorMessage = 'Failed to save project: ';
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        errorMessage += 'Network error. The project might be too large for the server to handle.';
      } else if (error.status === 413 || error.message.includes('413')) {
        errorMessage += 'Project is too large. Try uploading fewer or smaller files.';
      } else if (error.status === 500) {
        errorMessage += 'Server error. The project might be too large or contain problematic data.';
      } else {
        errorMessage += error.message || 'Unknown error';
      }

      errorMessage += '\n\nTry reducing the number of files or selecting only source code files.';
      alert(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const shouldExcludeFile = (file) => {
    // Get the file path (webkitRelativePath for directory uploads, name for individual files)
    const filePath = file.webkitRelativePath || file.name;

    // Directories and patterns to exclude
    const excludePatterns = [
      'node_modules/',
      '__pycache__/',
      '.git/',
      '.svn/',
      '.hg/',
      'venv/',
      'env/',
      '.env/',
      'dist/',
      'build/',
      '.next/',
      '.nuxt/',
      'target/',
      'bin/',
      'obj/',
      '.idea/',
      '.vscode/',
      '.DS_Store',
      'Thumbs.db',
      '.cache/',
      'coverage/',
      '.pytest_cache/',
      '__MACOSX/',
      '.egg-info/',
      'vendor/',
    ];

    // Check if file path contains any excluded pattern
    return excludePatterns.some(pattern => filePath.includes(pattern));
  };

  const processFiles = async (fileList) => {
    setIsProcessing(true);
    const newFiles = [];

    // Filter out excluded files
    const filteredFiles = Array.from(fileList).filter(file => !shouldExcludeFile(file));

    console.log(`Processing ${filteredFiles.length} files (filtered from ${fileList.length} total)`);

    // Hard limit to prevent browser crashes
    const MAX_FILES = 500;
    if (filteredFiles.length > MAX_FILES) {
      alert(`Too many files! Found ${filteredFiles.length} files after filtering.\n\nMaximum allowed: ${MAX_FILES} files\n\nPlease select a smaller folder or use a more specific subdirectory.\n\nTip: Select only the specific source code folders you need (e.g., 'src/', 'lib/', 'app/') instead of the entire project root.`);
      setIsProcessing(false);
      return;
    }

    for (const file of filteredFiles) {
      try {
        const content = await file.text();
        newFiles.push({
          name: file.webkitRelativePath || file.name, // Use full path for folder uploads
          content,
          size: file.size,
          type: file.type || 'text/plain',
        });
      } catch (error) {
        console.error(`Error reading file ${file.name}:`, error);
      }
    }

    setFiles(prev => [...prev, ...newFiles]);
    setIsProcessing(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    processFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    processFiles(selectedFiles);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3b82f6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#64748b]">Loading your project...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Header */}
      <header className="border-b border-[#1e293b] bg-[#0f172a]/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Code2 className="w-8 h-8 text-[#3b82f6]" />
                <Zap className="w-4 h-4 text-[#8b5cf6] absolute -bottom-1 -right-1" />
              </div>
              <div>
                <h1 className="text-xl font-bold">TriggerDocs</h1>
                <p className="text-xs text-[#64748b]">AI-powered code documentation</p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/repositories')}
                className="flex items-center gap-2 px-4 py-2 bg-[#8b5cf6] hover:bg-[#7c3aed] text-white rounded-lg transition-colors"
              >
                <GitBranch className="w-4 h-4" />
                <span>Connect GitHub</span>
              </button>
              {isSaving ? (
                <div className="flex items-center gap-2 text-[#64748b] text-sm">
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : files.length > 0 && (
                <button
                  onClick={saveProject}
                  className="flex items-center gap-2 px-4 py-2 bg-[#3b82f6] hover:bg-[#2563eb] text-white rounded-lg transition-colors"
                >
                  <Save className="w-4 h-4" />
                  <span>Save Project</span>
                </button>
              )}
              <div className="flex items-center gap-2 text-[#cbd5e1] text-sm">
                <User className="w-4 h-4" />
                <span>{user?.email}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-[#1e293b] hover:bg-[#334155] text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Project Name */}
        <div className="mb-6">
          <input
            type="text"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            onBlur={saveProject}
            className="text-2xl font-bold bg-transparent border-none outline-none text-white placeholder-[#64748b] focus:text-[#3b82f6] transition-colors"
            placeholder="Project Name"
          />
        </div>

        {/* Upload Area */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleUploadClick}
          className={`border-2 border-dashed rounded-2xl p-12 text-center cursor-pointer transition-all ${
            isDragging
              ? 'border-[#3b82f6] bg-[#3b82f6]/10'
              : 'border-[#1e293b] hover:border-[#334155] hover:bg-[#0f172a]/50'
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            webkitdirectory="true"
            directory="true"
            onChange={handleFileInput}
            className="hidden"
            accept=".js,.jsx,.ts,.tsx,.py,.java,.cpp,.c,.h,.cs,.rb,.go,.rs,.php,.html,.css,.md,.txt"
          />

          <Upload className={`w-16 h-16 mx-auto mb-4 ${isDragging ? 'text-[#3b82f6]' : 'text-[#64748b]'}`} />

          <h3 className="text-xl font-semibold mb-2">
            {isDragging ? 'Drop your files here' : 'Upload code files'}
          </h3>

          <p className="text-[#64748b] mb-4">
            Drag and drop or click to select files
          </p>

          <div className="flex flex-wrap gap-2 justify-center">
            {['.js', '.ts', '.py', '.java', '.cpp', '.go', '.rs'].map(ext => (
              <span key={ext} className="px-3 py-1 bg-[#1e293b] text-[#94a3b8] rounded-full text-sm">
                {ext}
              </span>
            ))}
          </div>
        </div>

        {/* Processing Indicator */}
        {isProcessing && (
          <div className="mt-6 flex items-center justify-center gap-3 text-[#3b82f6]">
            <div className="w-5 h-5 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin"></div>
            <span>Processing files...</span>
          </div>
        )}

        {/* Files List */}
        {files.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#3b82f6]" />
              Uploaded Files ({files.length})
            </h2>

            <div className="grid gap-3">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="bg-[#0f172a] border border-[#1e293b] rounded-lg p-4 hover:border-[#334155] transition-all group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-[#3b82f6] flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">{file.name}</h3>
                        <p className="text-sm text-[#64748b]">
                          {formatFileSize(file.size)} • {file.type}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => removeFile(index)}
                      className="px-3 py-1 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors opacity-0 group-hover:opacity-100"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {files.length === 0 && !isProcessing && (
          <div className="mt-12 text-center">
            <Code2 className="w-16 h-16 text-[#334155] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#64748b] mb-2">No files uploaded yet</h3>
            <p className="text-[#475569]">Upload your code files to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;